import type { SidebarNavSection, SidebarNavItem, SidebarNavLeafItem } from "./sidebarNav";
import { SIDEBAR_NAV_SECTIONS } from "./sidebarNav";
import type { ChatEntry } from "@/types/chat";

export type WorkspacePersona =
  | "leader"
  | "district_leader"
  | "teacher"
  | "support_staff"
  | "classified_staff";

/** Human-readable English labels for each persona. Shared by onboarding tiles and the profile screen. */
export const PERSONA_LABELS: Record<WorkspacePersona, string> = {
  district_leader: "District leader",
  leader: "School leader",
  teacher: "Classroom teacher",
  support_staff: "Paraprofessional / support staff",
  classified_staff: "Classified / office staff",
};

/** Personas with the same nav/access as `support_staff` (paraprofessional-level). */
const SUPPORT_LIKE_PERSONAS = new Set<WorkspacePersona>(["support_staff", "classified_staff"]);

/** Persisted `users.workspace_persona` from complete profile (nullable for legacy → leader). */
export function deriveWorkspacePersona(
  profile: { workspace_persona?: string | null } | null | undefined
): WorkspacePersona {
  const p = profile?.workspace_persona;
  if (
    p === "teacher" ||
    p === "support_staff" ||
    p === "leader" ||
    p === "district_leader" ||
    p === "classified_staff"
  ) {
    return p;
  }
  return "leader";
}

/** Sidebar nav item IDs hidden for classroom educators (everyone except leader). */
const TEACHER_EXCLUDED_ITEM_IDS = new Set<string>([
  "think-decision",
  "behavior-incident",
  "lab-board",
  "my-teams",
  "invite",
  "renew-spark",
]);

const SUPPORT_EXTRA_EXCLUDED_IDS = new Set<string>(["lab-parents"]);

export const PARENTS_CHAT_ENTRY = "strengthen_my_voice" as const;

export const NAVIGATE_PARENT_RELATIONSHIPS_CHAT_ENTRY = "navigate_parent_relationships" as const;

export const COLLEAGUES_CHAT_ENTRY = "lead_with_clarity" as const;

export const LEADING_YOUR_STAFF_CHAT_ENTRY = "leading_your_staff" as const;

export const BOARD_CHAT_ENTRY = "navigate_board_relationships" as const;

export const RENEW_SPARK_CHAT_ENTRY = "renew_my_spark" as const;

const LEADER_ONLY_CHAT_ENTRIES = new Set<string>([
  BOARD_CHAT_ENTRY,
  NAVIGATE_PARENT_RELATIONSHIPS_CHAT_ENTRY,
  LEADING_YOUR_STAFF_CHAT_ENTRY,
  RENEW_SPARK_CHAT_ENTRY,
]);

const LEADER_PERSONAS = new Set<WorkspacePersona>(["leader", "district_leader"]);

function excludedNavIds(persona: WorkspacePersona): Set<string> {
  if (persona === "leader" || persona === "district_leader") return new Set();
  const base = TEACHER_EXCLUDED_ITEM_IDS;
  if (persona === "teacher") return base;
  // support_staff + classified_staff: same exclusions (paraprofessional-level access)
  return new Set([...base, ...SUPPORT_EXTRA_EXCLUDED_IDS]);
}

function transformLeafForPersona(item: SidebarNavLeafItem, persona: WorkspacePersona): SidebarNavLeafItem {
  if (persona !== "leader" && persona !== "district_leader") return item;
  if (item.kind === "chat" && item.id === "lab-parents") {
    return {
      ...item,
      label: "Navigate Parent Relationships",
      entry: NAVIGATE_PARENT_RELATIONSHIPS_CHAT_ENTRY,
    };
  }
  if (item.kind === "chat" && item.id === "lab-colleagues") {
    return {
      ...item,
      label: "Leading Your Staff",
      entry: LEADING_YOUR_STAFF_CHAT_ENTRY,
    };
  }
  return item;
}

function filterNavItemForPersona(item: SidebarNavItem, persona: WorkspacePersona, excluded: Set<string>): SidebarNavItem | null {
  if (item.kind === "group") {
    const children = item.children
      .filter((child) => !excluded.has(child.id))
      .map((child) => transformLeafForPersona(child, persona));
    if (children.length === 0) return null;
    return { ...item, children };
  }

  if (excluded.has(item.id)) return null;
  return transformLeafForPersona(item, persona);
}

export function getSidebarSectionsForPersona(persona: WorkspacePersona): SidebarNavSection[] {
  const excluded = excludedNavIds(persona);
  return SIDEBAR_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .map((item) => filterNavItemForPersona(item, persona, excluded))
      .filter((item): item is SidebarNavItem => item !== null),
  })).filter((s) => s.items.length > 0);
}

const TEACHER_LIKE_RESTRICT_PATHS_EXACT = new Set([
  "/tools/think-through-decision",
  "/conversation-lab/board",
  "/admin/my-teams",
  "/admin/invite-teammates",
  "/supports/renew-my-spark",
]);

const TEACHER_LIKE_REST_PATH_PREFIXES = ["/restoration-room", "/admin/my-teams"];

/** If the user hits a URL blocked for their persona, send them home. */
export function getPersonaRestrictedRedirect(persona: WorkspacePersona, pathname: string): string | null {
  if (persona === "leader" || persona === "district_leader") return null;

  const path = pathname.replace(/\/+$/, "") || "/";

  for (const prefix of TEACHER_LIKE_REST_PATH_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return "/";
    }
  }

  if (TEACHER_LIKE_RESTRICT_PATHS_EXACT.has(path)) {
    return "/";
  }

  return null;
}

export function isLeaderPersona(persona: WorkspacePersona): boolean {
  return LEADER_PERSONAS.has(persona);
}

/** Map teacher-facing lab entries to leader spaces when appropriate. */
export function resolveChatEntryForPersona(entry: ChatEntry, persona: WorkspacePersona): ChatEntry {
  if (isLeaderPersona(persona) && entry === PARENTS_CHAT_ENTRY) {
    return NAVIGATE_PARENT_RELATIONSHIPS_CHAT_ENTRY;
  }
  if (isLeaderPersona(persona) && entry === COLLEAGUES_CHAT_ENTRY) {
    return LEADING_YOUR_STAFF_CHAT_ENTRY;
  }
  return entry;
}

/** Hub quick chips that map to gated tools — hide for teacher & support_staff. */
export const HUB_GATED_CHIP_IDS = new Set<string>(["decision", "incident"]);

export function heroChipVisibleForPersona(persona: WorkspacePersona, chipId: string): boolean {
  if (persona === "leader" || persona === "district_leader") return true;
  return !HUB_GATED_CHIP_IDS.has(chipId);
}

export function isMentorEntryBlockedForPersona(persona: WorkspacePersona, entry: string): boolean {
  if (SUPPORT_LIKE_PERSONAS.has(persona) && entry === PARENTS_CHAT_ENTRY) return true;
  if (isLeaderPersona(persona) && entry === PARENTS_CHAT_ENTRY) return true;
  if (isLeaderPersona(persona) && entry === COLLEAGUES_CHAT_ENTRY) return true;
  if (!isLeaderPersona(persona) && LEADER_ONLY_CHAT_ENTRIES.has(entry)) {
    return true;
  }
  return false;
}
